/**
 * Collects information about SKU view/basket status and passes to
 * the SDK to be stored in a tenant-side cookie.
 *
 */
import type { Middleware } from 'redux';
import { RootState } from '../../store';
import * as Actions from '../actions/app';
import * as Selectors from '../selectors';

/**
 * A single entry in the cookie.
 * sku = id
 * price = unit price for sku
 * status = whether item was just viewed in 3dx, or added to client basket via 3dx
 * lastUpdated = timestamp for this entry
 *
 */
export type SkuCookieEntry = {
    sku: string;
    price: number;
    status: 'Viewed' | 'Basket';
    solution: '3DX';
    lastUpdated: string;
};

export let skuDataEvent: (skuCookieEntries: Record<string, SkuCookieEntry>) => void = () => {};
export const initialiseSkuDataCollection = (
    skuDataEventHandler: (skuCookieEntries: Record<string, SkuCookieEntry>) => void,
) => {
    skuDataEvent = skuDataEventHandler;
};

/**
 * Takes the current basket products,
 * turns each into an skuEntry,
 * sends the array to the SDK to update the cookie.
 *
 */
const updateCookie = (
    basketProducts: Array<{
        productId: string;
        quantity: number;
        unitPrice: number;
    }>,
    status: 'Viewed' | 'Basket',
): void => {
    const newCookieEntries = basketProducts.reduce((record, product) => {
        const sku = product.productId;
        record[sku] = {
            sku: sku,
            price: product.unitPrice,
            status: status,
            solution: '3DX',
            lastUpdated: Date.now().toString(),
        };
        return record;
    }, {} as Record<string, SkuCookieEntry>);
    skuDataEvent(newCookieEntries);
};

export const middleware: Middleware =
    ({ getState }) =>
    next =>
    action => {
        const appState: RootState = getState();
        const basketProducts = Selectors.Basket.products(appState);

        if (!basketProducts) {
            // This will happen when clients don't have basket enabled.
            return next(action);
        }

        switch (action.type) {
            case Actions.skuView:
                updateCookie(basketProducts, 'Viewed');
                break;
            case Actions.addToBasket:
                updateCookie(basketProducts, 'Basket');
                break;
        }

        return next(action);
    };
